"use client";

import { useEffect, useRef } from "react";
import clsx from "clsx";
import type { GraphState, Node, Edge } from "./types";
import { step, settle, defaultLayoutParams } from "./forceLayout";

/**
 * InterconnectedWeb renders a deterministic Canvas force-graph driven by the
 * GraphState produced from the event log. The simulation runs in canonical
 * [-1, 1] space; the renderer maps to canvas px each frame, so RTL flipping
 * and full-bleed takeover are pure presentation concerns.
 *
 * Per the plan's "Visual Hook" section:
 *   - cold nodes: muted, no glow
 *   - verified nodes: brand-teal accent + halo (--state-verified-glow)
 *   - traced nodes: lighter teal + halo
 *   - financed nodes: warm-gold accent + halo
 *   - HCS anchors: small pulsing diamonds along edges
 *   - bank nodes: logo-anchored hubs
 *   - candidate edges: dashed until trade.ProposalAccepted
 */

type Props = {
  graph: GraphState;
  className?: string;
  rtl?: boolean;
};

const COLOURS = {
  cold:           "#3A4D4D",
  verified:       "#4FA0A0",
  traced:         "#6FB8B8",
  financed:       "#C9A227",
  ink:            "#0E1A1A",
  white:          "#E8EFEF",
  muted:          "#6B7C7C",
  edgeCold:       "rgba(58,77,77,0.45)",
  edgeVerified:   "rgba(79,160,160,0.55)",
  edgeTraced:     "rgba(111,184,184,0.65)",
  edgeFinanced:   "rgba(201,162,39,0.78)",
  hcsAnchor:      "#6FB8B8",
} as const;

function nodeAccent(n: Node): string {
  switch (n.state) {
    case "verified": return COLOURS.verified;
    case "traced":   return COLOURS.traced;
    case "financed": return COLOURS.financed;
    default:         return COLOURS.cold;
  }
}

function edgeAccent(e: Edge): string {
  switch (e.state) {
    case "verified": return COLOURS.edgeVerified;
    case "traced":   return COLOURS.edgeTraced;
    case "financed": return COLOURS.edgeFinanced;
    default:         return COLOURS.edgeCold;
  }
}

export function InterconnectedWeb({ graph, className, rtl = false }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const settledRef = useRef(false);

  // Refs hold the latest graph + rtl so the RAF loop can read them every
  // frame without re-mounting the effect when they change. This is the
  // critical fix for the flicker: WebRail bumps a `version` state on every
  // SSE event, producing a new `graph` reference. If the animation effect
  // depended on `[graph, rtl]`, it tore down + restarted (cancel RAF,
  // disconnect ResizeObserver, re-resize, re-settle, re-attach RAF) on
  // every event — visible as a flicker at high event rates.
  const graphRef = useRef(graph);
  const rtlRef   = useRef(rtl);
  graphRef.current = graph;
  rtlRef.current   = rtl;

  // Settle once on mount so the first paint is stable rather than exploding
  // outwards from the seed positions.
  useEffect(() => {
    if (settledRef.current) return;
    settle(graphRef.current, 90, { ...defaultLayoutParams });
    settledRef.current = true;
    // intentionally empty deps — settle once for the seed layout
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Animation loop — mounts once, reads graph + rtl from refs per frame.
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    let raf = 0;
    let lastT = performance.now();

    const resize = () => {
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const { width, height } = container.getBoundingClientRect();
      canvas.width  = Math.floor(width  * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width  = `${width}px`;
      canvas.style.height = `${height}px`;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    const tick = (t: number) => {
      const g = graphRef.current;
      const dt = Math.min(0.05, (t - lastT) / 1000) * 60; // normalise to ~60fps units
      lastT = t;
      step(g, dt, { ...defaultLayoutParams, centrePair: g.centrePair });
      draw();
      raf = requestAnimationFrame(tick);
    };

    const draw = () => {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const g = graphRef.current;
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const W = canvas.width;
      const H = canvas.height;

      ctx.clearRect(0, 0, W, H);
      ctx.save();
      ctx.scale(dpr, dpr);

      // Faint canvas-internal wash. Reduced from 0.05 → 0.02 alpha so the
      // rail no longer reads as a teal strip when the body canvas-wash
      // already passes a stronger gradient through this region.
      const w = W / dpr;
      const h = H / dpr;
      const wash = ctx.createRadialGradient(w * 0.78, h * 0.22, 10, w * 0.78, h * 0.22, w * 0.6);
      wash.addColorStop(0, "rgba(79,160,160,0.02)");
      wash.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = wash;
      ctx.fillRect(0, 0, w, h);

      // Map canonical (-1..1) to canvas px with margin, applying RTL mirror.
      const margin = Math.min(w, h) * 0.10;
      const sx = (w - margin * 2) / 2;
      const sy = (h - margin * 2) / 2;
      const ox = w / 2;
      const oy = h / 2;
      const rtlSign = rtlRef.current ? -1 : 1;
      const project = (x: number, y: number) => ({
        px: ox + rtlSign * x * sx,
        py: oy + y * sy,
      });

      // Edges first (so nodes draw atop).
      for (const e of g.edges.values()) {
        const a = g.nodes.get(e.from);
        const b = g.nodes.get(e.to);
        if (!a || !b) continue;
        const A = project(a.x, a.y);
        const B = project(b.x, b.y);
        ctx.lineWidth = 0.6 + e.weight * 4.0;
        ctx.strokeStyle = edgeAccent(e);
        if (e.candidate) {
          ctx.setLineDash([4, 4]);
          ctx.globalAlpha = 0.7;
        } else {
          ctx.setLineDash([]);
          ctx.globalAlpha = 0.95;
        }
        ctx.beginPath();
        ctx.moveTo(A.px, A.py);
        ctx.lineTo(B.px, B.py);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;

        // HCS anchor diamonds + recent-pulse halo.
        for (const an of e.hcsAnchors) {
          const ax = A.px + (B.px - A.px) * an.along;
          const ay = A.py + (B.py - A.py) * an.along;
          const since = Math.max(0, performance.now() - an.emittedAt);
          const pulse = since < 1200 ? 1 - since / 1200 : 0;
          const r = 3 + pulse * 6;
          ctx.fillStyle = COLOURS.hcsAnchor;
          ctx.globalAlpha = 0.85 + pulse * 0.15;
          ctx.beginPath();
          ctx.moveTo(ax, ay - r);
          ctx.lineTo(ax + r, ay);
          ctx.lineTo(ax, ay + r);
          ctx.lineTo(ax - r, ay);
          ctx.closePath();
          ctx.fill();
          if (pulse > 0) {
            ctx.globalAlpha = 0.18 * pulse;
            ctx.beginPath();
            ctx.arc(ax, ay, r * 3.5, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.globalAlpha = 1;
        }

        // Bank-badge annotation midway along an LC edge.
        if (e.bankName && e.kind === "lc") {
          const mx = (A.px + B.px) / 2;
          const my = (A.py + B.py) / 2;
          const label = e.bankFundingPct ? `${e.bankFundingPct}% ${e.bankName}` : e.bankName;
          ctx.font = "600 9px var(--font-mono, monospace)";
          const m = ctx.measureText(label);
          ctx.fillStyle = "rgba(14,26,26,0.92)";
          ctx.strokeStyle = COLOURS.financed;
          ctx.lineWidth = 0.8;
          const padX = 6, padY = 3;
          ctx.beginPath();
          ctx.roundRect(mx - m.width / 2 - padX, my - 7 - padY, m.width + padX * 2, 14 + padY * 2, 7);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = COLOURS.financed;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(label, mx, my);
        }
      }

      // Nodes.
      const now = performance.now();
      for (const n of g.nodes.values()) {
        const { px, py } = project(n.x, n.y);
        const pulse = n.pulseUntil > now ? (n.pulseUntil - now) / 1500 : 0;

        // Glow halo (alpha-modulated parent token, per Demo Semantic Layer).
        if (n.state !== "cold" || pulse > 0) {
          const haloR = 18 + pulse * 28;
          const haloAlpha = pulse > 0
            ? 0.12 + pulse * 0.18
            : (n.state === "financed" ? 0.18 : 0.10);
          ctx.beginPath();
          ctx.arc(px, py, haloR, 0, Math.PI * 2);
          ctx.fillStyle = withAlpha(nodeAccent(n), haloAlpha);
          ctx.fill();
        }

        // Body.
        let r = 9;
        if (n.kind === "LegalEntity")     r = 12;
        if (n.kind === "Bank")            r = 13;
        if (n.kind === "LcNode")          r = 8;
        if (n.kind === "GleifCluster")    r = 10;
        if (n.kind === "AttestationPartner") r = 8;

        // outer ring
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.fillStyle = COLOURS.ink;
        ctx.fill();
        ctx.lineWidth = 1.6;
        ctx.strokeStyle = nodeAccent(n);
        ctx.stroke();

        // initials / glyph
        const fontSize = Math.max(8, r * 0.85);
        ctx.font = `600 ${fontSize}px var(--font-display, system-ui)`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = n.state === "cold" ? COLOURS.muted : nodeAccent(n);
        const initials = (n.initials ?? n.label).slice(0, n.kind === "GleifCluster" ? 5 : 3);
        ctx.fillText(initials, px, py + 0.5);

        // jurisdiction flag chip
        if (n.flag) {
          const fr = 6;
          const fx = px + r * 0.85;
          const fy = py + r * 0.85;
          ctx.beginPath();
          ctx.arc(fx, fy, fr, 0, Math.PI * 2);
          ctx.fillStyle = COLOURS.ink;
          ctx.fill();
          ctx.strokeStyle = "rgba(79,160,160,0.45)";
          ctx.lineWidth = 0.8;
          ctx.stroke();
          ctx.font = "600 7px var(--font-mono, monospace)";
          ctx.fillStyle = "#B8DCDC";
          ctx.fillText(n.flag, fx, fy + 0.3);
        }
      }

      ctx.restore();
    };

    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
    // intentionally empty deps — the loop reads graph + rtl from refs.
    // Without this, every SSE event triggers a full teardown + restart,
    // visible as a flicker at high event rates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={containerRef} className={clsx("relative h-full w-full", className)}>
      <canvas ref={canvasRef} className="h-full w-full" />
    </div>
  );
}

function withAlpha(hex: string, a: number): string {
  // accept #RRGGBB or rgba(...) — return rgba(r,g,b,a)
  if (hex.startsWith("rgba")) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}
